import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast } from '../ToastContext';
import { useAuth } from '../AuthContext';

function providerLabel(p) {
  const x = String(p || '').toLowerCase();
  if (x === 'gmail') return 'Gmail';
  if (x === 'microsoft' || x === 'outlook') return 'Outlook';
  if (x === 'imap') return 'IMAP';
  return p || 'Email';
}

/**
 * Decide tab — read-only summary of the current decision state for an RFQ.
 *
 * The actual commit UI lives in the Compare tab (per-cell Pick + Qty + Reason).
 * This tab gives the user a single place to review:
 *   • what's been awarded so far (per line, per supplier, with qty + price + total)
 *   • what's still outstanding (lines with remaining qty)
 *   • totals across the RFQ
 */
/**
 * Decision revision strip (Feature B).
 *
 * Shows Rev 1 → Rev 2 → … for this RFQ, what changed between them, and starts a
 * revision by handing the current awards back to Compare pre-selected.
 */
function DecisionRevisionStrip({ rfqId, revData, onRevise }) {
  const [openRev, setOpenRev] = useState(null);
  if (!revData) return null;

  const all = [...(revData.superseded || []), ...(revData.current || [])].sort(
    (a, b) => (a.revision || 1) - (b.revision || 1) || new Date(a.createdAt) - new Date(b.createdAt)
  );
  if (all.length === 0) return null;

  // Only offer "Revise" when there is exactly one live decision to revise; with
  // several additive awards in force the user should pick one on the list below.
  const reviseTarget = (revData.current || []).length === 1 ? revData.current[0] : null;
  const hasHistory = (revData.superseded || []).length > 0;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b>Decision revisions</b>
        {all.map((d) => {
          const isCurrent = d.isCurrent !== false;
          return (
            <span
              key={d._id}
              className={'chip ' + (isCurrent ? 'ready' : openRev === d._id ? 'info' : 'muted')}
              style={{ cursor: 'pointer', fontWeight: isCurrent ? 700 : 400 }}
              onClick={() => setOpenRev(openRev === d._id ? null : d._id)}
              title={isCurrent ? 'Current award' : 'Superseded — click to see what changed'}
            >
              Rev {d.revision || 1}
              {d.createdAt ? ` · ${new Date(d.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
              {isCurrent ? ' · current' : ''}
            </span>
          );
        })}
        <div style={{ flex: 1 }} />
        {reviseTarget && (
          <button className="btn btn-secondary btn-sm" onClick={() => onRevise(reviseTarget)}>
            Revise decision →
          </button>
        )}
      </div>

      {/* Standing warning: somebody has already been told they won. */}
      {revData.requiresRenotification && (
        <div className="banner review" style={{ marginTop: 10 }}>
          <b>Order confirmations already sent</b> — revising this award means re-notifying:{' '}
          {(revData.orderConfirmationsSent || []).map((s) => s.name).join(', ')}.
        </div>
      )}

      {/* What changed, for whichever revision is expanded. */}
      {openRev && (() => {
        const d = all.find((x) => String(x._id) === String(openRev));
        if (!d) return null;
        const changes = d.changeSummary || [];
        return (
          <div style={{ marginTop: 12 }}>
            <div className="subtitle">
              Rev {d.revision || 1}
              {d.revisionReason ? ` — ${d.revisionReason}` : ''}
            </div>
            {changes.length === 0 ? (
              <div className="subtitle" style={{ marginTop: 6 }}>
                {d.revision > 1 ? 'No line-level changes recorded.' : 'Original award — nothing superseded.'}
              </div>
            ) : (
              <ul style={{ margin: '8px 0 0 18px', fontSize: 13 }}>
                {changes.map((c, i) => (
                  <li key={i}>
                    <b>Line {c.lineNumber}</b>
                    {c.partNumber ? ` (${c.partNumber})` : ''}:{' '}
                    {c.changeType === 'SupplierChanged' && <>{c.fromSupplierName} → {c.toSupplierName}</>}
                    {c.changeType === 'QuantityChanged' && (
                      <>{c.toSupplierName} · {c.fromQuantity} → {c.toQuantity}</>
                    )}
                    {c.changeType === 'Added' && <>added {c.toSupplierName} · {c.toQuantity}</>}
                    {c.changeType === 'Removed' && <>removed {c.fromSupplierName} · {c.fromQuantity}</>}
                    {c.changeType === 'ReasonChanged' && <>reason updated ({c.toSupplierName})</>}
                    {c.reason ? <span style={{ color: 'var(--ink-500)' }}> · {c.reason}</span> : null}
                  </li>
                ))}
              </ul>
            )}
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 8, wordBreak: 'break-all' }}>
              hash {d.hash}
            </div>
          </div>
        );
      })()}

      {hasHistory && (
        <div className="subtitle" style={{ marginTop: 8 }}>
          Superseded revisions are kept for audit — the RFQ header and Customer Quote use the current one only.
        </div>
      )}
    </div>
  );
}

export default function Decide({ rfqId }) {
  const [comp, setComp] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [revData, setRevData] = useState(null);
  const [err, setErr] = useState(null);
  // Track per-supplier "send order confirmation" busy state
  const [notifyBusy, setNotifyBusy] = useState({}); // supplierId -> bool
  const [notifySent, setNotifySent] = useState({}); // supplierId -> messageId or true
  // Mailbox connector to send order confirmations from
  const [connectors, setConnectors] = useState([]);
  const [selectedConnectorId, setSelectedConnectorId] = useState(null);
  const toast = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.comparison(rfqId),
      // Current revisions only — a superseded award must not be counted again in
      // awardsByLine below (Feature B).
      api.listCurrentDecisions(rfqId).then((r) => r.items || r || []),
      api.listSuppliers().then((r) => r.items || []),
      api.listConnectors().then((r) => (r?.items || r || []).filter((c) => c.status === 'connected')).catch(() => []),
      api.decisionRevisions(rfqId).catch(() => null),
    ])
      .then(([c, d, s, conns, revs]) => {
        setComp(c); setDecisions(d); setSuppliers(s); setConnectors(conns); setRevData(revs);
        // Default connector: user preference, else first connected, else null (server falls back to SES)
        const prefId = user?.preferences?.defaultDispatchConnectorId;
        if (prefId && conns.find((c) => String(c._id) === String(prefId))) {
          setSelectedConnectorId(String(prefId));
        } else if (conns.length > 0) {
          setSelectedConnectorId(String(conns[0]._id));
        }
      })
      .catch((e) => setErr(e.message));
  }, [rfqId, user?.preferences?.defaultDispatchConnectorId]);

  // Build per-line awards: lineId -> [{ supplierId, supplierName, quantity, unitPrice, decidedAt, reason }]
  const awardsByLine = useMemo(() => {
    if (!comp) return {};
    const supplierById = Object.fromEntries(suppliers.map((s) => [String(s._id), s]));
    const out = {};
    for (const d of decisions) {
      for (const sel of d.selections || []) {
        const lid = String(sel.rfqLineItemId);
        if (!out[lid]) out[lid] = [];
        // Find unit price from the comparison matrix's cellsBySupplier
        const lineRow = comp.lineMatrix.find(({ line }) => String(line._id) === lid);
        const cell = lineRow?.cellsBySupplier?.[String(sel.supplierId)];
        const unitPrice = cell?.quoteLine?.unitPrice ?? null;
        const supplierName = supplierById[String(sel.supplierId)]?.name
          || cell?.supplier?.name
          || String(sel.supplierId).slice(-6);
        out[lid].push({
          supplierId: String(sel.supplierId),
          supplierName,
          quantity: Number(sel.quantity) || (Number(lineRow?.line?.quantity) || 0),
          unitPrice,
          decidedAt: d.decidedAt || d.createdAt,
          reason: sel.reason,
        });
      }
    }
    return out;
  }, [comp, decisions, suppliers]);

  if (err) return <div className="banner risk">{err}</div>;
  if (!comp) return <div className="empty">Loading…</div>;

  const totalRequired = comp.lineMatrix.reduce((a, { line }) => a + (Number(line.quantity) || 0), 0);
  const totalDecided = comp.lineMatrix.reduce((a, { line }) => a + (Number(line.decidedQuantity) || 0), 0);
  const totalOutstanding = Math.max(0, totalRequired - totalDecided);
  const grandTotal = comp.lineMatrix.reduce((acc, { line }) => {
    const awards = awardsByLine[String(line._id)] || [];
    return acc + awards.reduce((a, w) => a + (w.quantity * (w.unitPrice || 0)), 0);
  }, 0);

  const hasAnyAward = decisions.length > 0;

  // Group awards by supplier so we can show one "Notify" action per winning supplier
  const awardsBySupplier = {};
  for (const lid in awardsByLine) {
    const line = comp.lineMatrix.find(({ line: l }) => String(l._id) === lid)?.line;
    for (const w of awardsByLine[lid]) {
      if (!awardsBySupplier[w.supplierId]) awardsBySupplier[w.supplierId] = { supplierId: w.supplierId, supplierName: w.supplierName, lines: [], total: 0 };
      awardsBySupplier[w.supplierId].lines.push({ ...w, lineNumber: line?.lineNumber, partNumber: line?.partNumber });
      awardsBySupplier[w.supplierId].total += w.quantity * (w.unitPrice || 0);
    }
  }
  const winningSuppliers = Object.values(awardsBySupplier);

  async function notifySupplier(supplierId) {
    setNotifyBusy((b) => ({ ...b, [supplierId]: true }));
    try {
      const r = await api.sendOrderConfirmation(rfqId, { supplierId, connectorId: selectedConnectorId || undefined });
      setNotifySent((s) => ({ ...s, [supplierId]: r.messageId || true }));
      toast.push(`Order confirmation sent to ${r.supplierName} · ${r.awardedLines} line${r.awardedLines !== 1 ? 's' : ''}`);
    } catch (e) {
      toast.push(e.message || 'Failed to send order confirmation', { variant: 'error' });
    } finally {
      setNotifyBusy((b) => ({ ...b, [supplierId]: false }));
    }
  }

  /**
   * Start a revision: confirm the consequences, then hand the current awards to
   * Compare pre-selected so the user edits the award rather than rebuilding it.
   * The reason is collected on Compare at commit time, where the final awards are
   * known — asking for it here would be premature.
   */
  async function startRevision(target) {
    try {
      const pre = await api.revisionPreflight(rfqId, target?._id);
      const warn = pre.requiresRenotification
        ? `\n\nOrder confirmations were already sent to: ${(pre.orderConfirmationsSent || [])
            .map((s) => s.name)
            .join(', ')}.\nThose suppliers will need to be re-notified.`
        : '';
      const ok = window.confirm(
        `Revise this decision?\n\nRev ${pre.revision} stays in the audit trail; your changes commit as ` +
          `Rev ${pre.nextRevision} and will need a reason.${warn}`
      );
      if (!ok) return;
      // Compare reads these to pre-select the existing awards.
      sessionStorage.setItem(
        `rivet_revision_${rfqId}`,
        JSON.stringify({
          revisesDecisionId: pre.decisionId,
          nextRevision: pre.nextRevision,
          prefillSelections: pre.prefillSelections,
          orderConfirmationsSent: pre.orderConfirmationsSent || [],
        })
      );
      navigate(`/rfq/${rfqId}/compare?revise=${pre.decisionId}`);
    } catch (e) {
      toast.push(e.message || 'Could not start a revision', { variant: 'error' });
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h2 className="card-title" style={{ margin: 0 }}>Decision summary</h2>
        <span className="chip muted" style={{ fontSize: 11 }}>read-only</span>
        <div style={{ flex: 1 }} />
        <Link to={`/rfq/${rfqId}/compare`}>
          <button className="btn btn-sm">Decide more in Compare →</button>
        </Link>
      </div>

      <DecisionRevisionStrip rfqId={rfqId} revData={revData} onRevise={startRevision} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <SummaryStat label="Lines" value={comp.lineMatrix.length} />
        <SummaryStat label="Quantity required" value={totalRequired} />
        <SummaryStat label="Quantity decided" value={`${totalDecided}/${totalRequired}`} variant={totalDecided === totalRequired ? 'ready' : 'review'} />
        <SummaryStat label="Outstanding pcs" value={totalOutstanding} variant={totalOutstanding === 0 ? 'ready' : 'review'} />
      </div>

      {!hasAnyAward && (
        <div className="empty">
          <h3>No decisions committed yet</h3>
          <p>Open the <b>Compare</b> tab and pick a supplier on each line to start awarding quantities.</p>
        </div>
      )}

      {hasAnyAward && (
        <>
          {/* Notify suppliers — one row per winning supplier */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Notify winning suppliers</h2>
              <span className="chip muted" style={{ fontSize: 11 }}>{winningSuppliers.length} supplier{winningSuppliers.length !== 1 ? 's' : ''}</span>
              <div style={{ flex: 1 }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ color: 'var(--ink-500)' }}>Send from</span>
                <select
                  className="select"
                  value={selectedConnectorId || ''}
                  onChange={(e) => setSelectedConnectorId(e.target.value || null)}
                  style={{ minWidth: 220 }}
                >
                  {connectors.length === 0 && <option value="">(no mailbox connected — using CNERZY default)</option>}
                  {connectors.map((c) => (
                    <option key={c._id} value={c._id}>
                      {providerLabel(c.provider)} · {c.displayEmail || c.imapUser || c.email || c._id.slice(-6)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 4, marginBottom: 12 }}>
              Sends an order-confirmation email with the awarded lines + qty + price + an Excel attachment from the selected mailbox. Replies land back in that inbox so the thread stays linked.
              {connectors.length === 0 && <> <Link to="/settings/email-inbox">Connect Gmail or Outlook</Link> to send from your own address.</>}
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>Lines awarded</th>
                  <th>Total qty</th>
                  <th>Order value</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {winningSuppliers.map((s) => {
                  const totalQty = s.lines.reduce((a, l) => a + l.quantity, 0);
                  const sent = notifySent[s.supplierId];
                  const busy = !!notifyBusy[s.supplierId];
                  return (
                    <tr key={s.supplierId}>
                      <td><b>{s.supplierName}</b></td>
                      <td className="mono">{s.lines.length}</td>
                      <td className="mono">{totalQty} pcs</td>
                      <td className="mono">${s.total.toFixed(2)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {sent ? (
                          <span className="chip ready" style={{ fontSize: 11 }}>✓ Sent</span>
                        ) : (
                          <button className="btn btn-sm" disabled={busy} onClick={() => notifySupplier(s.supplierId)}>
                            {busy ? 'Sending…' : 'Send order confirmation'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h2 className="card-title">Per-line awards</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Part</th>
                  <th>Required</th>
                  <th>Awarded</th>
                  <th>Outstanding</th>
                  <th>Suppliers</th>
                  <th>Line value</th>
                </tr>
              </thead>
              <tbody>
                {comp.lineMatrix.map(({ line }) => {
                  const lid = String(line._id);
                  const awards = awardsByLine[lid] || [];
                  const required = Number(line.quantity) || 0;
                  const awarded = awards.reduce((a, w) => a + w.quantity, 0);
                  const outstanding = Math.max(0, required - awarded);
                  const lineValue = awards.reduce((a, w) => a + w.quantity * (w.unitPrice || 0), 0);
                  const isComplete = required > 0 && awarded >= required;
                  return (
                    <tr key={lid}>
                      <td>{line.lineNumber}</td>
                      <td className="mono">{line.partNumber}</td>
                      <td className="mono">{required}</td>
                      <td className="mono">{awarded}</td>
                      <td className="mono">
                        {outstanding > 0
                          ? <span className="chip review" style={{ fontSize: 11 }}>{outstanding}</span>
                          : isComplete
                          ? <span className="chip ready" style={{ fontSize: 11 }}>Complete</span>
                          : '—'}
                      </td>
                      <td>
                        {awards.length === 0 && <span style={{ color: 'var(--ink-500)', fontSize: 12 }}>—</span>}
                        {awards.map((w, i) => (
                          <div key={i} style={{ fontSize: 12, marginBottom: 2 }}>
                            <b>{w.supplierName}</b>
                            <span className="mono" style={{ color: 'var(--ink-500)', marginLeft: 6 }}>{w.quantity} pcs</span>
                            {w.unitPrice ? <span className="mono" style={{ color: 'var(--ink-500)', marginLeft: 6 }}>@ ${Number(w.unitPrice).toFixed(2)}</span> : null}
                            {w.reason && <span style={{ color: 'var(--ink-500)', marginLeft: 6 }}>· {w.reason}</span>}
                          </div>
                        ))}
                      </td>
                      <td className="mono">{lineValue ? `$${lineValue.toFixed(2)}` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ fontWeight: 600 }}>Total</td>
                  <td className="mono" style={{ fontWeight: 600 }}>{totalRequired}</td>
                  <td className="mono" style={{ fontWeight: 600 }}>{totalDecided}</td>
                  <td className="mono" style={{ fontWeight: 600 }}>{totalOutstanding}</td>
                  <td />
                  <td className="mono" style={{ fontWeight: 600 }}>${grandTotal.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="card section-bg">
            <h2 className="card-title">Decision history</h2>
            <div style={{ fontSize: 12, color: 'var(--ink-500)', marginBottom: 8 }}>
              {decisions.length} decision{decisions.length !== 1 ? 's' : ''} committed. See the <Link to={`/rfq/${rfqId}/audit`}>Audit tab</Link> for full details + immutable hashes.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {decisions.map((d) => (
                <div key={d._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#fff', borderRadius: 6, border: '1px solid var(--border-1)' }}>
                  <span className="chip muted" style={{ fontSize: 11 }}>{d.mode}</span>
                  <span style={{ fontSize: 12 }}>{new Date(d.decidedAt || d.createdAt).toLocaleString()}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>·</span>
                  <span style={{ fontSize: 12 }}>{(d.selections || []).length} selection{(d.selections || []).length !== 1 ? 's' : ''}</span>
                  {d.overallReason && <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>· {d.overallReason}</span>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryStat({ label, value, variant }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--ink-500)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
      {variant && <span className={'chip ' + variant} style={{ fontSize: 10, marginTop: 4 }}>{variant === 'ready' ? '✓ Done' : 'In progress'}</span>}
    </div>
  );
}
